// src/services/SurveyService.js
import API_CONFIG from "./ApiConfig";

const SurveyService = {
  /**
   * Fetches a user's previous responses for a specific survey
   *
   * @param {string} surveyId - The ID of the survey
   * @param {string} token - Firebase authentication token
   * @returns {Promise<Object>} - Survey response data if it exists
   */
  getSurveyResponsesBySurveyId: async (surveyId, token) => {
    try {
      if (!surveyId || !token) {
        throw new Error("Survey ID and authentication token are required");
      }

      const url = `${API_CONFIG.BASE_URL}/api/surveys/response/${surveyId}`;

      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "x-user-token": token,
          "Access-Control-Allow-Origin": "*",
          "Access-Control-Allow-Headers":
            "Origin, X-Requested-With, Content-Type, Accept, x-user-token",
        },
      });

      // If no responses exist, the API might return 404
      if (response.status === 404) {
        return null;
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message ||
            `Server responded with status: ${response.status}`
        );
      }

      return await response.json();
    } catch (error) {
      console.error("Error fetching survey responses:", error);
      // Re-throw to allow the component to handle "no responses" scenario
      throw error;
    }
  },
  /**
   * Fetch a survey by ID
   * @param {string} surveyId - The ID of the survey to fetch
   * @param {string} firebaseToken - Authentication token
   * @returns {Promise<Object>} - Survey data
   */
  getSurveyById: async (surveyId, firebaseToken) => {
    try {
      const url = `${API_CONFIG.BASE_URL}/api/surveys/${surveyId}`;

      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "x-user-token": firebaseToken,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch survey: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error("Error fetching survey:", error);
      throw error;
    }
  },

  /**
   * Submit survey responses
   * @param {string} surveyId - The ID of the survey
   * @param {string} userId - User ID
   * @param {Array} responses - Array of question responses
   * @param {string} firebaseToken - Authentication token
   * @returns {Promise<Object>} - Submission result
   */
  submitSurveyResponses: async (surveyId, userId, responses, firebaseToken) => {

    try {
      const url = `${API_CONFIG.BASE_URL}/api/surveys`;

      const payload = {
        surveyTemplateId: surveyId,
        userId: userId,
        responses: responses,
      };

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-user-token": firebaseToken,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`Failed to submit survey: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error("Error submitting survey responses:", error);
      throw error;
    }
  },

  /**
   * Submit survey responses
   * @param {string} surveyId - The ID of the survey
   * @param {string} userId - User ID
   * @param {Array} responses - Array of question responses
   * @param {string} firebaseToken - Authentication token
   * @returns {Promise<Object>} - Submission result
   */
  submitSurveyResponsesForExtendedWarranty: async (
    surveyId,
    userId,
    responses,
    firebaseToken
  ) => {
    try {
      const url = `${API_CONFIG.BASE_URL}/api/extended-warranty/survey`;

      const payload = {
        surveyTemplateId: surveyId,
        userId: userId,
        responses: responses,
      };

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-user-token": firebaseToken,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`Failed to submit survey: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error("Error submitting survey responses:", error);
      throw error;
    }
  },

  /**
   * Submit a completed pre-auth survey for an authenticated user
   *
   * @param {string} surveyId - The ID of the survey
   * @param {Object} answers - The survey answers in the format stored in sessionStorage
   * @param {Object} questions - The survey questions structure
   * @param {string} firebaseToken - Authentication token
   * @returns {Promise<Object>} - Submission result
   */
  submitPreAuthSurvey: async (surveyId, answers, questions, firebaseToken) => {
    try {
      if (!surveyId || !answers || !questions || !firebaseToken) {
        throw new Error(
          "Survey ID, answers, questions, and auth token are required"
        );
      }

      // Import formatSurveyResponses from utils
      const { formatSurveyResponses } = require("../utils/SurveyProcessor");

      // Format the responses using your existing utility
      const formattedResponses = formatSurveyResponses(answers, questions);

      // Use the existing submitSurveyResponses function
      return await SurveyService.submitSurveyResponses(
        surveyId,
        "",
        formattedResponses,
        firebaseToken
      );
    } catch (error) {
      console.error("Error submitting pre-auth survey:", error);
      throw error;
    }
  },

  /**
   * Submit contact preferences to the insurance profile endpoint
   * @param {string} surveyId - The ID of the survey
   * @param {string} dataBuyerId - The ID of the data buyer
   * @param {Array} responses - Array of question responses
   * @param {string} token - Authentication token
   * @returns {Promise<Object>} - Submission result
   */
  submitInsuranceProfileSurvey: async (
    surveyId,
    dataBuyerId,
    responses,
    token
  ) => {
    try {
      if (!surveyId || !token) {
        throw new Error("Survey ID and authentication token are required");
      }

      // Build the API URL with the data buyer ID parameter
      const url = `${API_CONFIG.BASE_URL}/api/surveys/insurance-profile?dataBuyerId=${dataBuyerId}`;

      // Create the request body according to OwnliCreateSurveyResponseDto format
      const requestBody = {
        surveyTemplateId: surveyId,
        responses: responses,
      };

      // Make the API call
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-user-token": token,
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message ||
            `Server responded with status: ${response.status}`
        );
      }

      return await response.json();
    } catch (error) {
      console.error("Error submitting insurance profile survey:", error);
      throw error;
    }
  },
};

export default SurveyService;
